import { useIsReadOnly } from '@/lib/casl'
import { truncateFieldValueToCharacterLimit } from '@/lib/field-character-limits'
import { useFieldContext } from '@/lib/form-contexts'
import { cn, normalizeEmailInput } from '@/lib/utils'
import { Input, type InputProps } from '../ui/input'
import { Label } from '../ui/label'
import FieldInfo from './FieldInfo'
import { Typography } from '@/components/ui/typography'
import { type ReactNode, useId } from 'react'

export type EmailInputProps = InputProps & {
  label?: ReactNode
  readOnlyEmptyText?: ReactNode
  characterMaxLength?: number | null
}

export default function EmailInput({
  id,
  label = 'Email',
  placeholder = 'me@example.com',
  className = '',
  readOnlyEmptyText = 'Not provided',
  disabled = false,
  characterMaxLength = null,
  ...props
}: EmailInputProps) {
  const field = useFieldContext<string>()
  const { isReadOnly } = useIsReadOnly()
  const randomId = useId()
  const inputId = id || `${randomId}-${field.name}`

  return (
    <div className={cn('flex flex-col gap-y-2 w-full', className)}>
      {!!label && (
        <div className="inline-flex items-center">
          <Label htmlFor={inputId}>{label}</Label>
        </div>
      )}

      {isReadOnly ? (
        <Typography variant="label" tone={!field.state.value ? 'muted' : 'default'}>
          {field.state.value || readOnlyEmptyText}
        </Typography>
      ) : (
        <>
          <Input
            id={inputId}
            type="email"
            placeholder={placeholder ?? 'me@email.com'}
            value={field.state.value}
            onChange={(e) =>
              field.handleChange(
                truncateFieldValueToCharacterLimit(
                  normalizeEmailInput(e.target.value),
                  characterMaxLength
                )
              )
            }
            onBlur={field.handleBlur}
            className="w-full"
            disabled={disabled}
            autoComplete="email"
            {...props}
          />
          <FieldInfo />
        </>
      )}
    </div>
  )
}
